import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Stack, Avatar, Chip, CircularProgress } from '@mui/material';
import { EmojiEvents, ScubaDiving, Timer } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { buddyService, LeaderboardEntry } from '../services/buddyService';
import { OptionsManager } from '../utils/optionsManager';
import { formatDepth } from '../utils/unitConversions';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

interface Category {
  label: string;
  icon: React.ReactElement;
  value: (e: LeaderboardEntry) => number | null | undefined;
  format: (value: number) => string;
}

/**
 * Light gamification, buddy-scoped only (not a global leaderboard) — reuses the same dive
 * stats each buddy already sees on the other's profile, so there's no new privacy exposure.
 * Shown as a panel above the feed alongside DiscoverClubsPanel, matching its compact style.
 */
const LeaderboardPanel: React.FC<{ userId: string }> = ({ userId }) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const units = OptionsManager.getInstance().getOption('units');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    buddyService.getLeaderboard(userId)
      .then(results => { if (!cancelled) setEntries(results); })
      .catch(() => { if (!cancelled) setEntries([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const categories: Category[] = [
    { label: 'Most dives logged', icon: <ScubaDiving fontSize="small" color="primary" />, value: e => e.diveCount, format: v => `${v} dive${v === 1 ? '' : 's'}` },
    { label: 'Deepest dive', icon: <EmojiEvents fontSize="small" color="warning" />, value: e => e.maxDepthMeters, format: v => formatDepth(v, units.depth) },
    { label: 'Longest bottom time', icon: <Timer fontSize="small" color="info" />, value: e => e.longestBottomTimeMinutes, format: v => `${Math.round(v)} min` },
  ];

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  if (entries.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Buddy Leaderboard</Typography>
      <Stack spacing={2}>
        {categories.map(category => {
          const ranked = entries
            .map(e => ({ entry: e, value: category.value(e) }))
            .filter((r): r is { entry: LeaderboardEntry; value: number } => r.value != null)
            .sort((a, b) => b.value - a.value)
            .slice(0, 3);
          if (ranked.length === 0) return null;

          return (
            <Box key={category.label}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                {category.icon}
                <Typography variant="caption" sx={{ fontWeight: 600 }} color="text.secondary">
                  {category.label}
                </Typography>
              </Stack>
              <Stack spacing={0.75}>
                {ranked.map(({ entry, value }, i) => (
                  <Stack
                    key={entry.userId}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, p: 0.5 }}
                    onClick={() => navigate(`/u/${entry.userId}`)}
                  >
                    <Typography variant="caption" sx={{ width: 16, fontWeight: 700, color: 'text.secondary' }}>
                      {i + 1}
                    </Typography>
                    <Avatar
                      src={entry.profilePhotoUrl ?? undefined}
                      sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 10, fontWeight: 700 }}
                    >
                      {!entry.profilePhotoUrl && (initials(entry.displayName) || '?')}
                    </Avatar>
                    <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>{entry.displayName}</Typography>
                    <Chip size="small" variant="outlined" label={category.format(value)} />
                  </Stack>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default LeaderboardPanel;
